/**
 * The preview: the chain, approximated in the browser, live.
 *
 * ## What it is, and is not
 *
 * The engine renders the real master, offline, with a multiband compressor,
 * a harmonic exciter, a mid/side stage with mono bass, two-pass loudness
 * normalisation and an oversampled true-peak limiter. None of that exists
 * in WebAudio. What does exist is close enough to hear *where a tile is
 * going* before paying for a render: the corrective curve and the three
 * knobs as biquads (exact), the compression as one wideband compressor set
 * from the mid band (approximate), saturation as a soft waveshaper
 * (approximate), width as mid/side with the side high-passed at the
 * bass-mono split (close), and loudness as the gain that would take the
 * track's measured LUFS to the target, followed by a hard compressor
 * standing in for the limiter (approximate: it does not see true peaks).
 *
 * So: the preview is for choosing a tile and setting the slider and the
 * knobs by ear. The master is what you download. The page says so.
 *
 * ## One source, two paths
 *
 * `createMediaElementSource` is a one-way door: once the element is tapped,
 * its sound only reaches the speakers through the graph. So the original
 * has a dry path (a gain) and a wet path (the chain), both into the
 * destination, and Original / Preview is which gain is up. Both paths run
 * from the same element, so they cannot drift.
 */
import type { Chain } from './api';

const DEFAULT_INPUT_LUFS = -18;

interface Graph {
  context: AudioContext;
  source: MediaElementAudioSourceNode;
  dry: GainNode;
  wet: GainNode;
  curve: BiquadFilterNode[];
  low: BiquadFilterNode;
  mid: BiquadFilterNode;
  high: BiquadFilterNode;
  compressor: DynamicsCompressorNode;
  shaper: WaveShaperNode;
  shaperDry: GainNode;
  shaperWet: GainNode;
  splitter: ChannelSplitterNode;
  merger: ChannelMergerNode;
  midFromL: GainNode;
  midFromR: GainNode;
  sideFromL: GainNode;
  sideFromR: GainNode;
  sideHighpass: BiquadFilterNode;
  sideGain: GainNode;
  outLFromMid: GainNode;
  outLFromSide: GainNode;
  outRFromMid: GainNode;
  outRFromSide: GainNode;
  makeup: GainNode;
  limiter: DynamicsCompressorNode;
}

const tapped = new WeakMap<HTMLMediaElement, Graph>();

/*
 * One context for the page. A browser allows a handful of contexts per
 * tab, and a desk that made one per element would run out after a few
 * track changes and go silent for the rest of the session.
 */
let shared: AudioContext | null = null;

function sharedContext(): AudioContext {
  shared ??= new AudioContext();

  return shared;
}

/**
 * Build the graph for an element, once. The element must carry
 * `crossOrigin="anonymous"`, or the tap is silence: a tainted media element
 * yields zeros.
 */
export function attach(element: HTMLMediaElement): Graph {
  const existing = tapped.get(element);

  if (existing) return existing;

  const context = sharedContext();
  const source = context.createMediaElementSource(element);

  const dry = context.createGain();
  const wet = context.createGain();

  // [1] the corrective curve: ten peaking filters at the curve's centres.
  const curve = [31, 63, 125, 250, 500, 1000, 2000, 4000, 8000, 16000].map((hz) => {
    const f = context.createBiquadFilter();

    f.type = 'peaking';
    f.frequency.value = hz;
    f.Q.value = 1.1;
    f.gain.value = 0;

    return f;
  });

  // The three knobs, as the engine defines them.
  const low = context.createBiquadFilter();

  low.type = 'lowshelf';
  low.frequency.value = 100;

  const mid = context.createBiquadFilter();

  mid.type = 'peaking';
  mid.frequency.value = 1000;
  mid.Q.value = 0.7;

  const high = context.createBiquadFilter();

  high.type = 'highshelf';
  high.frequency.value = 8000;

  // [2] one compressor standing in for three.
  const compressor = context.createDynamicsCompressor();

  compressor.knee.value = 6;

  // [3] saturation: a soft curve, oversampled, mixed against the dry.
  const shaper = context.createWaveShaper();

  shaper.oversample = '4x';

  const shaperDry = context.createGain();
  const shaperWet = context.createGain();

  // [4] width: mid/side, the side high-passed at the bass-mono split.
  const splitter = context.createChannelSplitter(2);
  const merger = context.createChannelMerger(2);
  const midFromL = context.createGain();
  const midFromR = context.createGain();
  const sideFromL = context.createGain();
  const sideFromR = context.createGain();
  const sideHighpass = context.createBiquadFilter();
  const sideGain = context.createGain();
  const outLFromMid = context.createGain();
  const outLFromSide = context.createGain();
  const outRFromMid = context.createGain();
  const outRFromSide = context.createGain();

  midFromL.gain.value = 0.5;
  midFromR.gain.value = 0.5;
  sideFromL.gain.value = 0.5;
  sideFromR.gain.value = -0.5;
  sideHighpass.type = 'highpass';
  sideHighpass.Q.value = 0.7;
  outLFromMid.gain.value = 1;
  outLFromSide.gain.value = 1;
  outRFromMid.gain.value = 1;
  outRFromSide.gain.value = -1;

  // [5] level, then a hard compressor as the ceiling.
  const makeup = context.createGain();
  const limiter = context.createDynamicsCompressor();

  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.08;

  // Wire: source → dry → out; source → curve… → knobs → compressor → shaper mix → m/s → makeup → limiter → wet → out.
  source.connect(dry);
  dry.connect(context.destination);

  let node: AudioNode = source;

  for (const f of curve) {
    node.connect(f);
    node = f;
  }

  node.connect(low);
  low.connect(mid);
  mid.connect(high);
  high.connect(compressor);

  compressor.connect(shaperDry);
  compressor.connect(shaper);
  shaper.connect(shaperWet);

  const afterSat = context.createGain();

  shaperDry.connect(afterSat);
  shaperWet.connect(afterSat);

  afterSat.connect(splitter);
  splitter.connect(midFromL, 0);
  splitter.connect(midFromR, 1);
  splitter.connect(sideFromL, 0);
  splitter.connect(sideFromR, 1);

  const midSum = context.createGain();
  const sideSum = context.createGain();

  midFromL.connect(midSum);
  midFromR.connect(midSum);
  sideFromL.connect(sideSum);
  sideFromR.connect(sideSum);
  sideSum.connect(sideHighpass);
  sideHighpass.connect(sideGain);

  midSum.connect(outLFromMid);
  sideGain.connect(outLFromSide);
  midSum.connect(outRFromMid);
  sideGain.connect(outRFromSide);
  outLFromMid.connect(merger, 0, 0);
  outLFromSide.connect(merger, 0, 0);
  outRFromMid.connect(merger, 0, 1);
  outRFromSide.connect(merger, 0, 1);

  merger.connect(makeup);
  makeup.connect(limiter);
  limiter.connect(wet);
  wet.connect(context.destination);

  const graph: Graph = {
    context,
    source,
    dry,
    wet,
    curve,
    low,
    mid,
    high,
    compressor,
    shaper,
    shaperDry,
    shaperWet,
    splitter,
    merger,
    midFromL,
    midFromR,
    sideFromL,
    sideFromR,
    sideHighpass,
    sideGain,
    outLFromMid,
    outLFromSide,
    outRFromMid,
    outRFromSide,
    makeup,
    limiter,
  };

  tapped.set(element, graph);
  hear(graph, 'original');

  return graph;
}

/** Which path is up. Ramped over a few milliseconds so the switch does not click. */
export function hear(graph: Graph, side: 'original' | 'preview'): void {
  const t = graph.context.currentTime;

  graph.dry.gain.cancelScheduledValues(t);
  graph.wet.gain.cancelScheduledValues(t);
  graph.dry.gain.setTargetAtTime(side === 'original' ? 1 : 0, t, 0.005);
  graph.wet.gain.setTargetAtTime(side === 'preview' ? 1 : 0, t, 0.005);
}

/** Set every node from a composed chain. Cheap; called on every slider tick. */
export function apply(graph: Graph, chain: Chain, inputLufs: number | null): void {
  const t = graph.context.currentTime;
  const ramp = (param: AudioParam, value: number) => {
    param.cancelScheduledValues(t);
    param.setTargetAtTime(value, t, 0.02);
  };

  // [1] the curve, read at each filter's centre.
  const gains = gainsAt(chain.eq_curve, graph.curve.map((f) => f.frequency.value));

  graph.curve.forEach((f, i) => ramp(f.gain, gains[i] ?? 0));
  ramp(graph.low.gain, chain.user_eq.low_db);
  ramp(graph.mid.gain, chain.user_eq.mid_db);
  ramp(graph.high.gain, chain.user_eq.high_db);

  // [2] the mid band's numbers stand for the whole thing.
  const band = chain.multiband.bands[1] ?? chain.multiband.bands[0];

  if (chain.bypass.multiband || !band) {
    ramp(graph.compressor.threshold, 0);
    ramp(graph.compressor.ratio, 1);
  } else {
    ramp(graph.compressor.threshold, band.threshold_db);
    ramp(graph.compressor.ratio, band.ratio);
    ramp(graph.compressor.attack, band.attack_ms / 1000);
    ramp(graph.compressor.release, band.release_ms / 1000);
  }

  // [3] amount is the wet share; drive is how hard the curve bends.
  const amount = chain.bypass.saturation ? 0 : Math.min(1, chain.saturation.amount / 2);

  graph.shaper.curve = softCurve(chain.saturation.drive);
  ramp(graph.shaperDry.gain, 1 - amount);
  ramp(graph.shaperWet.gain, amount);

  // [4] side gain above the split; nothing below it.
  ramp(graph.sideHighpass.frequency, chain.width.bass_mono_hz);
  ramp(graph.sideGain.gain, chain.bypass.width ? 1 : db(chain.width.side_gain_db));

  // [5] the gain that takes measured to target, then the ceiling.
  const from = inputLufs ?? DEFAULT_INPUT_LUFS;
  const makeupDb = Math.max(-12, Math.min(18, chain.loudness.target_lufs - from));

  ramp(graph.makeup.gain, db(makeupDb));
  ramp(graph.limiter.threshold, chain.loudness.true_peak_dbtp - 1);
}

/** The element's context, resumed: a context made before a gesture starts suspended. */
export async function wake(graph: Graph): Promise<void> {
  if (graph.context.state !== 'running') {
    await graph.context.resume();
  }
}

function db(value: number): number {
  return Math.pow(10, value / 20);
}

/** A tanh-shaped transfer, steeper with drive, unity at small signals. */
function softCurve(drive: number): Float32Array {
  const n = 1024;
  const out = new Float32Array(n);
  const k = Math.max(0.5, drive);

  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;

    out[i] = Math.tanh(k * x) / Math.tanh(k);
  }

  return out;
}

/** A curve's gains at other frequencies, over log frequency, flat beyond the ends. Mirrors `Chain::gainsAt`. */
function gainsAt(curve: [number, number][], at: number[]): number[] {
  const points = [...curve].sort((a, b) => a[0] - b[0]);

  if (points.length === 0) return at.map(() => 0);

  const first = points[0]!;
  const last = points[points.length - 1]!;

  return at.map((hz) => {
    if (hz <= first[0]) return first[1];
    if (hz >= last[0]) return last[1];

    for (let i = 1; i < points.length; i++) {
      const p = points[i]!;

      if (hz <= p[0]) {
        const q = points[i - 1]!;
        const x0 = Math.log(q[0]);
        const x1 = Math.log(p[0]);
        const f = x1 === x0 ? 0 : (Math.log(hz) - x0) / (x1 - x0);

        return q[1] + f * (p[1] - q[1]);
      }
    }

    return last[1];
  });
}

export type { Graph };
